import {
  AppIdentifier,
  BaseEnv,
  CookieConsent,
  EnvType,
  Frame,
  KeycloakAuth,
  Project,
  ReportConfig
} from '@aipb/gulliver/env/model';
import { isNil } from '@aipb/shared/util/other';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';

export const APP_CONFIG = new InjectionToken<BaseEnv>('APP_CONFIG');
export const IS_PRODUCTION = new InjectionToken<boolean>('IS_PRODUCTION');
export const APP_NAME = new InjectionToken<Project>('APP_NAME');

@Injectable({
  providedIn: 'root'
})
export class EnvService {
  private readonly _env: BaseEnv;

  constructor(
    @Inject(APP_CONFIG) config: BaseEnv,
    @Inject(IS_PRODUCTION) isProduction: boolean,
    @Optional() @Inject(APP_NAME) appName: string
  ) {
    this._env = config;

    if (!this._env) return;

    this._env.isProduction = isProduction;
    this._env.name = this._env.name || appName;
  }

  get googleAnalyticsId(): string | undefined {
    if (!this._env) return undefined;

    return this._env.googleAnalyticsId;
  }

  get cookieConsent(): CookieConsent | undefined {
    return this._env.cookieConsent;
  }

  get report(): ReportConfig {
    return (
      this._env.report || {
        linkColor: 'primary',
        toolbarEnabled: false
      }
    );
  }

  get overlayEnabled(): boolean {
    return this._env.overlayEnabled || false;
  }

  get coopEnabled(): boolean {
    return this._env.coopEnabled || false;
  }

  get isPersonalContentEnabled(): boolean {
    if (!this._env) return false;

    return ('personalContent' in this._env && !isNil(this._env.personalContent)) || false;
  }

  get favoriteApiBaseUrl(): string {
    const personalContent = this._env.personalContent;

    if (!personalContent) {
      return '';
    }

    return personalContent.apiUrl;
  }

  get dropboxWrapperApiBaseUrl(): string | null {
    const dropboxWrapperApiBaseUrl = this._env.dropboxWrapperApiBaseUrl;

    if (!dropboxWrapperApiBaseUrl) {
      return null;
    }

    return dropboxWrapperApiBaseUrl;
  }

  get auth(): KeycloakAuth | undefined {
    return this._env.auth;
  }

  get hasBrowser() {
    return this.hasApp('browser');
  }

  get hasBrowserMirador() {
    return this.hasApp('browser-mirador');
  }

  get hasDynamicGallery() {
    return this.hasApp('dynamic-gallery');
  }

  get applications() {
    return this._env.applications;
  }

  get scripts(): string[] {
    return this._env.scripts || [];
  }

  get frame(): Frame {
    return this._env.frame;
  }

  get name(): string {
    return this._env.name;
  }

  get env(): EnvType {
    return this._env.env;
  }

  get raw(): BaseEnv {
    return this._env;
  }

  public isProduction(): boolean {
    return this._env.isProduction;
  }

  private hasApp(appId: AppIdentifier): boolean {
    return appId in this._env.applications;
  }
}
